import { action, autorun, computed, makeObservable, observable } from 'mobx';

import { SOCKET_KYC_FORCED } from '../../config/constants';
import type { Profile, UserTutorial, UserTutorialType } from '../../services/auth';
import { auth } from '../../services/auth';
import { intercom } from '../../services/intercom';
import { socket } from '../../services/socket';
import { EnumKycStatus } from '../../services/types';
import { getErrorMessage } from '../../utils/errors';
import { type RootStore } from '../RootStore';
import { Loadable } from '../traits/Loadable';

export class UserStore extends Loadable {
  rootStore: RootStore;

  constructor(_rootStore: RootStore) {
    super();
    makeObservable(this);

    this.rootStore = _rootStore;

    autorun(() => {
      const forceKycHandler = this.handleForcedKyc.bind(this);

      if (this.rootStore.authStore.authorized) {
        socket.on(SOCKET_KYC_FORCED, forceKycHandler);
      } else {
        socket.off(SOCKET_KYC_FORCED, forceKycHandler);
      }
    });
  }

  private handleForcedKyc() {
    if (this.profile?.kycStatus === EnumKycStatus.notStarted) {
      this.updateProfile({ kycStatus: EnumKycStatus.requested });
    }
  }

  public resetUserProfile() {
    this.setUserProfile(null);
  }

  @observable
  public profile: Profile | null = null;

  @action
  public setUserProfile(data: Profile | null) {
    this.profile = data;
  }

  @computed
  public get isInEurope() {
    const { countryCode: userCountryCode } = this.profile?.territory ?? {};
    const isUserInEurope = userCountryCode ? ['DE', 'AT'].includes(userCountryCode) : false;
    const { isLocationInEurope } = this.rootStore.liveGeoStore;

    return isUserInEurope || isLocationInEurope;
  }

  public async fetchProfile(ignoreCache?: boolean): Promise<Profile | null> {
    this.setLoading(true);
    try {
      const user = await auth.getUserProfile(ignoreCache);
      this.setUserProfile(user);

      return user;
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      console.error(`Failed to fetch user profile: ${errorMessage}`);
      this.resetUserProfile();
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  public updateProfile(data: Partial<Profile>) {
    if (!this.profile) {
      console.error('Attempt to modify user profile that is not yet fetched');
      return;
    }

    const user = { ...this.profile, ...data };

    intercom.updateUserData(user);
    this.setUserProfile(user);

    if (user.isOnboardingCompleted && !this.rootStore.onboardingStore.finishedFtue) {
      this.rootStore.onboardingStore.finishFtue();
    }
  }

  public markTutorialAsFinished(tutorialType: UserTutorialType) {
    if (!this.profile) {
      console.error('Attempt to modify user profile that is not yet fetched');
      return;
    }

    let newUserTutorials: UserTutorial[] = [];

    if (this.profile.userTutorials) {
      const isExisting = this.profile.userTutorials.some((tut) => tut.type === tutorialType);
      if (isExisting) {
        newUserTutorials = this.profile.userTutorials.map((tut) =>
          tut.type === tutorialType ? { ...tut, finishedAt: new Date() } : tut,
        );
      } else {
        const newUserTutorial: UserTutorial = {
          id: crypto.randomUUID(),
          userId: this.profile.userId,
          type: tutorialType,
          finishedAt: new Date(),
        };
        newUserTutorials = [...this.profile.userTutorials, newUserTutorial];
      }
    }

    this.updateProfile({ userTutorials: newUserTutorials });
  }
}
